import React, { useContext } from "react";
import { Dropdown } from "antd";
import { EditOutlined } from "@ant-design/icons";
// API
import { updateOnEditCell } from "api/pricerApi";
// Context
import { GlobalContext } from "contexts/GlobalContext";
import { PricerContext } from "contexts/PricerContext";
// Helpers
import { updatedPricerData } from "helpers/Pricer/helperAggFunc";
import { isSublegRow } from "helpers/Pricer/helperFunc";
import { isSummaryLevel } from "helpers/generalHelper";
import { onPricerCellEditingStarted } from "helpers/Pricer/utils";
import { pricerWorkingDate } from "helpers/utcCalendarDate";
// Constants
import { FIELD_NAMES } from "constants/pricerTable";

/**
 * Prefer row.option_types; fall back through nested summary_agg / leaves.
 * AG Grid summary groups often surface leg aggData first.
 */
function resolveTypeCellRowData(node) {
  const primary = node.group ? node.aggData : node.data;
  if (primary?.option_types != null) {
    return primary;
  }
  if (primary?.summary_agg?.option_types != null) {
    return primary.summary_agg;
  }

  const leaves = node.allLeafChildren;
  if (leaves?.length) {
    for (let i = 0; i < leaves.length; i += 1) {
      const data = leaves[i].data;
      if (data?.summary_agg?.option_types != null) {
        return data.summary_agg;
      }
    }
  }

  return primary ?? {};
}

/**
 * Summary type cell for agGroupCellRenderer.
 * Uses type-wrapper + Dropdown (not Select) so it fits the group expander cell.
 * Options come only from row.option_types — never GET /option-types.
 *
 * @see docs/features/pricer/option-type-change-frontend.md
 */
const OptionTypeDropdown = (params) => {
  const node = params.node;
  const rowData = resolveTypeCellRowData(node);

  const {
    state: { pricerTablesData, pricerRefAgGrid, dateSelected },
    refetchOptionRow,
    setIsOptionRowLoading,
    setPricerData,
    setIsCellEditing,
    setRowEditingData,
  } = useContext(PricerContext);
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);

  if (isSublegRow(node)) {
    return "";
  }

  const typeLabel = rowData?.[FIELD_NAMES.TYPE] ?? "";
  const optionTypes = Array.isArray(rowData?.option_types)
    ? rowData.option_types
    : [];
  const canChangeType = isSummaryLevel(params) && optionTypes.length > 0;

  if (!isSummaryLevel(params)) {
    return <span>{typeLabel}</span>;
  }

  const dropdownItems = optionTypes.map((type) => ({
    label: type.label,
    key: String(type.value),
  }));

  const handleMenuItemClick = ({ key }) => {
    const optionTypeId = Number(key);
    if (!Number.isFinite(optionTypeId)) {
      return;
    }

    onPricerCellEditingStarted(params, setRowEditingData, dark_Theme);

    const bodyParams = {
      option_row_id: rowData.summary_id,
      summary_id: rowData.summary_id,
      option_type_id: optionTypeId,
      calculation_date: pricerWorkingDate(dateSelected),
      row_type: 0,
    };

    setIsCellEditing(true);
    setIsOptionRowLoading(true);

    updateOnEditCell(bodyParams)
      .then((res) => {
        const updatedData = updatedPricerData(res.data.data, pricerTablesData);
        setPricerData(updatedData);

        pricerRefAgGrid?.api?.refreshCells({
          force: true,
          columns: [FIELD_NAMES.TYPE],
        });
      })
      .catch(() => {
        refetchOptionRow();
      })
      .finally(() => {
        setIsOptionRowLoading(false);
        setIsCellEditing(false);
        setRowEditingData(null);
      });
  };

  return (
    <div className="type-wrapper action-buttons__container">
      <span>{typeLabel}</span>
      {canChangeType && (
        <span className="setting-dropdown">
          <Dropdown
            className="action-buttons__dropdown button-icon"
            overlayClassName="setting-dropdown-menu"
            menu={{
              items: dropdownItems,
              theme: dark_Theme ? "dark" : "light",
              onClick: handleMenuItemClick,
            }}
            trigger={["click"]}
          >
            <EditOutlined />
          </Dropdown>
        </span>
      )}
    </div>
  );
};

export default OptionTypeDropdown;
